import { SelectProps } from '@nextui-org/react'
import React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dropdown } from './Dropdown'

interface SelectOption {
    [key: string]: any
}

interface SelectControlProps<T extends FieldValues>
    extends Omit<SelectProps, 'onChange' | 'children'> {
    name: Path<T>
    control: Control<T>
    rules?: Record<string, any>
    options: SelectOption[]
    selectMode?: 'none' | 'single' | 'multiple'
    placeholder?: string
    labelKey?: string
    valueKey?: string
    isBoolean?: boolean
    className?: string
    onFirstOptionSelected?: (first: boolean) => void
    onChangeCustom?: (value: any) => void
    onBeforeChange?: (value: any) => Promise<boolean>
}

export function SelectControl<T extends FieldValues>({
    name,
    control,
    rules,
    options,
    selectMode = 'single',
    placeholder,
    labelKey = 'label',
    valueKey = 'value',
    isBoolean = false,
    className,
    onFirstOptionSelected,
    onChangeCustom,
    onBeforeChange,
    ...props
}: SelectControlProps<T>): React.ReactElement {
    const { t } = useTranslation()
    const [isValidating, setIsValidating] = React.useState(false)
    const [pendingValue, setPendingValue] = React.useState<any>(null)

    // Transform options to use specified keys
    const transformedOptions = options.map((option) => {
        const value = isBoolean
            ? option[valueKey]
            : String(option[valueKey] || '')
        const label = String(option[labelKey] || option[valueKey] || 'Unknown')

        return {
            value,
            label,
            originalOption: option
        }
    })

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <div>
                    <Dropdown
                        {...(field as any)}
                        {...(props as any)}
                        className={className ? className : ''}
                        placeholder={
                            placeholder
                                ? typeof placeholder === 'string' &&
                                  placeholder.includes('.')
                                    ? t(placeholder)
                                    : placeholder
                                : undefined
                        }
                        options={transformedOptions}
                        selectionMode={selectMode}
                        value={
                            pendingValue !== null
                                ? pendingValue
                                : (field.value ??
                                  (selectMode === 'multiple' ? [] : undefined))
                        }
                        error={error}
                        onChange={async (value: any) => {
                            // Establecer valor pendiente para mostrar visualmente mientras valida
                            setPendingValue(value)

                            // Validar ANTES de establecer el valor si hay onBeforeChange
                            if (onBeforeChange) {
                                setIsValidating(true)
                                try {
                                    const isValid = await onBeforeChange(value)

                                    if (!isValid) {
                                        // Si la validación falla, revertir al valor anterior
                                        setPendingValue(null)
                                        setIsValidating(false)

                                        return
                                    }
                                } catch (error) {
                                    console.error(
                                        'Error en validación previa:',
                                        error
                                    )
                                    // Revertir en caso de error
                                    setPendingValue(null)
                                    setIsValidating(false)

                                    return
                                }
                                setIsValidating(false)
                            }

                            // Si pasó la validación o no hay validación, establecer el valor
                            setPendingValue(null)

                            if (onChangeCustom) {
                                onChangeCustom(value)
                            }

                            field.onChange(value)
                            // Check if first option is selected
                            if (onFirstOptionSelected) {
                                if (value === transformedOptions[0]?.value) {
                                    onFirstOptionSelected(true)
                                } else {
                                    onFirstOptionSelected(false)
                                }
                            }
                        }}
                        isDisabled={isValidating || props.isDisabled}
                    />
                </div>
            )}
        />
    )
}
